'use client'

import { useState, useEffect } from "react"
import { ScrollingAnnouncementBar } from "@/components/dashboard/scrolling-announcement-bar"
import { GameInfoSection } from "@/components/dashboard/game-info-section"
import { NavigationMenu } from "@/components/dashboard/navigation-menu"
import { cn } from "@/lib/utils"
import { GenericTablePageContent } from "@/components/dashboard/generic-table-page-content"
import { renderDearSingleResultRowCells } from "@/components/dashboard/table-row-renderers"
import { fetchResultsList, fetchSessions } from "@/lib/api"

export default function Page() {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const [selectedGame, setSelectedGame] = useState("Dear Single")
  const [selectedSession, setSelectedSession] = useState("")
  const [sessions, setSessions] = useState<any[]>([])
  const [tableData, setTableData] = useState<any[]>([])
  const [loading, setLoading] = useState(false)

  const [username] = useState(() => {
    if (typeof window !== "undefined") {
      return localStorage.getItem("username") || "defaultUser"
    }
    return "defaultUser"
  })

  const tableHeaders = [
    { label: "Sr. No.", sortable: true },
    { label: "Date", sortable: true },
    { label: "Session", sortable: true },
    { label: "Dear-1st", sortable: true },
    { label: "Dear-2nd", sortable: true },
    { label: "Dear-3rd", sortable: true },
    { label: "Dear-4th", sortable: true },
    { label: "Dear-5th Last", sortable: true },
  ]

  const sessionTimes = ["ALL", "MOR", "DAY", "EVE"]

  // Fetch sessions
  useEffect(() => {
    const loadSessions = async () => {
      const sessionsData = await fetchSessions();
      setSessions(sessionsData);
      if (sessionsData.length > 0) {
        setSelectedSession(sessionsData[0].id);
      }
    };
    loadSessions();
  }, []);

  // Fetch results and transform for Dear Single display
  useEffect(() => {
    const loadResults = async () => {
      if (!selectedSession) return;
      
      setLoading(true);
      try {
        const data = await fetchResultsList(selectedSession);
        
        // Transform results for Dear Single display
        const dearResults = data.results.filter((result: any) => 
          result.category.includes('DEAR') || result.category.includes('SINGLE')
        );

        const formattedData = dearResults.map((result: any, index: number) => {
          const session = sessions.find(s => s.id === selectedSession);
          return {
            id: result.id,
            srNo: index + 1,
            date: new Date(result.createdAt).toLocaleDateString(),
            session: session?.name || 'Unknown',
            d1: result.category === 'DEAR_1ST' ? result.number : 0,
            d2: result.category === 'DEAR_2ND' ? result.number : 0,
            d3: result.category === 'DEAR_3RD' ? result.number : 0,
            d4: result.category === 'DEAR_4TH' ? result.number : 0,
            d5: result.category === 'DEAR_5TH' ? result.number : 0,
            rawData: result
          };
        });

        // If no results, show empty state
        if (formattedData.length === 0 && sessions.length > 0) {
          const session = sessions.find(s => s.id === selectedSession);
          formattedData.push({
            id: 'no-results',
            srNo: 1,
            date: session ? new Date(session.date).toLocaleDateString() : new Date().toLocaleDateString(),
            session: session?.name || 'Unknown',
            d1: '-',
            d2: '-',
            d3: '-',
            d4: '-',
            d5: '-',
            rawData: null
          });
        }

        setTableData(formattedData);
      } catch (error) {
        console.error("Error loading dear single results:", error);
      } finally {
        setLoading(false);
      }
    };

    loadResults();
  }, [selectedSession, sessions]);

  const handleSessionChange = (sessionId: string) => {
    setSelectedSession(sessionId);
  };

  return (
    <div className="min-h-screen bg-gray-100 flex flex-col">
      <header className="bg-gradient-to-r from-red-800 to-red-900 text-white py-3 px-4 sm:px-6 flex items-center justify-between shadow-md">
        <div className="flex items-center gap-4">
          <span className={cn("text-lg font-semibold", isMobileMenuOpen && "hidden sm:block")}>
            Three Circle 7 - Welcome: {username}
          </span>
        </div>
        <NavigationMenu isMobileMenuOpen={isMobileMenuOpen} setIsMobileMenuOpen={setIsMobileMenuOpen} />
      </header>

      <ScrollingAnnouncementBar text="🎯 Welcome to Three Circle 7 Lottery • Latest Results Available • Play Responsibly • Good Luck! • 🎯 Welcome to Three Circle 7 Lottery • Latest Results Available • Play Responsibly • Good Luck! •" />

      <main className="flex-1 p-3 sm:p-4">
        <div className="max-w-6xl mx-auto bg-white rounded-lg shadow-lg p-4">
          {/* Session Selector */}
          <div className="mb-4">
            <label htmlFor="session-select" className="block text-sm font-medium text-gray-700 mb-2">
              Select Session
            </label>
            <select
              id="session-select"
              value={selectedSession}
              onChange={(e) => handleSessionChange(e.target.value)}
              className="w-full md:w-64 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
            >
              <option value="">Select a session</option>
              {sessions.map((session) => (
                <option key={session.id} value={session.id}>
                  {session.name} - {new Date(session.date).toLocaleDateString()}
                </option>
              ))}
            </select>
          </div>

          <GameInfoSection selectedGame={selectedGame} onGameChange={setSelectedGame} />

          <GenericTablePageContent
            title="Dear Single Result"
            tableHeaders={tableHeaders}
            data={tableData}
            sessionOptions={sessionTimes}
            defaultStartDate={new Date("2025-07-04T00:00:00")}
            defaultEndDate={new Date("2025-08-04T00:00:00")}
            renderRowCells={renderDearSingleResultRowCells}
          />
        </div>
      </main>
    </div>
  )
}